<template>
  <form class="schema-form" @submit.prevent="submitForm">
    <PrimeCard>
      <template #title>{{ schema.title || readable(schema.table) }}</template>
      <template #content>
        <div class="grid">
          <div
            v-for="field in visibleFields"
            :key="field.name"
            class="field col-12 md:col-6"
          >
            <label :for="field.name">
              {{ field.label || readable(field.name) }}
              <span v-if="isRequired(field)" class="required">*</span>
            </label>

            <MultiSelect
              v-if="field.multiple"
              :id="field.name"
              v-model="formData[field.name]"
              :options="toOptions(field.options)"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              placeholder="Select..."
              :maxSelectedLabels="4"
            />

            <PrimeDropdown
              v-else-if="field.options && field.options.length"
              :id="field.name"
              v-model="formData[field.name]"
              :options="toOptions(field.options)"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              placeholder="Select..."
            />

            <InputNumber
              v-else-if="isNumber(field)"
              :id="field.name"
              v-model="formData[field.name]"
              class="w-full"
              :useGrouping="false"
              :mode="isCurrency(field) ? 'currency' : 'decimal'"
              :currency="isCurrency(field) ? currencyCode : undefined"
              locale="en-US"
            />

            <PrimeCalendar
              v-else-if="isDate(field)"
              :id="field.name"
              v-model="formData[field.name]"
              class="w-full"
              dateFormat="yy-mm-dd"
              showIcon
            />

            <TextArea
              v-else-if="isLongText(field)"
              :id="field.name"
              v-model="formData[field.name]"
              class="w-full"
              rows="4"
              autoResize
            />

            <PrimeInputText
              v-else
              :id="field.name"
              v-model="formData[field.name]"
              :type="inputType(field)"
              class="w-full"
              :maxlength="field.length || null"
            />

            <small v-if="errors[field.name]" class="error-msg">{{ errors[field.name] }}</small>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="form-actions">
          <PrimeButton type="button" severity="secondary" label="Reset" icon="pi pi-refresh" @click="resetForm" />
          <PrimeButton type="submit" :loading="submitting" label="Submit" icon="pi pi-check" />
        </div>
      </template>
    </PrimeCard>
  </form>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SchemaDrivenForm',
  props: {
    schema: {
      type: Object,
      required: true
    },
    submitUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['submitted'],
  data() {
    return {
      formData: {},
      errors: {},
      submitting: false
    };
  },
  computed: {
    visibleFields() {
      return (this.schema.columns || []).filter((field) => !field.primaryKey && !field.autoIncrement && !field.hidden);
    },
    currencyCode() {
      return this.formData.currency_used || 'GHS';
    }
  },
  watch: {
    schema: {
      immediate: true,
      deep: true,
      handler() {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.errors = {};
      this.formData = this.visibleFields.reduce((payload, field) => {
        if (field.multiple) {
          payload[field.name] = Array.isArray(field.default) ? [...field.default] : [];
        } else {
          payload[field.name] = field.default ?? '';
        }
        return payload;
      }, {});
    },
    submitForm() {
      if (!this.validateForm()) {
        return;
      }

      const endpoint = this.submitUrl || this.schema.submitUrl;
      const payload = this.serializePayload();

      if (!endpoint) {
        this.$emit('submitted', payload);
        return;
      }

      this.submitting = true;
      axios.post(endpoint, payload)
        .then((response) => {
          this.$emit('submitted', response.data);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    validateForm() {
      const errors = {};

      this.visibleFields.forEach((field) => {
        const value = this.formData[field.name];
        const emptyArray = Array.isArray(value) && value.length === 0;
        const emptyValue = value === '' || value === null || value === undefined || emptyArray;

        if (this.isRequired(field) && emptyValue) {
          errors[field.name] = `${field.label || this.readable(field.name)} is required`;
        }
      });

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    serializePayload() {
      return this.visibleFields.reduce((payload, field) => {
        const value = this.formData[field.name];

        if (this.isDate(field) && value instanceof Date) {
          payload[field.name] = value.toISOString().slice(0, 10);
        } else {
          payload[field.name] = value;
        }

        return payload;
      }, {});
    },
    isRequired(field) {
      return field.required === true || field.nullable === false;
    },
    isNumber(field) {
      return ['int', 'integer', 'bigint', 'smallint', 'decimal', 'float', 'double', 'numeric'].includes(this.normalizedType(field));
    },
    isCurrency(field) {
      return ['amount', 'revenue', 'turnover', 'income', 'price', 'cost'].some((word) => field.name.includes(word));
    },
    isDate(field) {
      return ['date', 'datetime', 'timestamp'].includes(this.normalizedType(field));
    },
    isLongText(field) {
      return ['text', 'longtext', 'mediumtext', 'json'].includes(this.normalizedType(field)) && !field.multiple;
    },
    inputType(field) {
      if (field.name.includes('email')) {
        return 'email';
      }
      if (field.name.includes('phone') || field.name.includes('mobile')) {
        return 'tel';
      }
      return 'text';
    },
    normalizedType(field) {
      return String(field.type || 'varchar').toLowerCase();
    },
    readable(value) {
      return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    toOptions(options = []) {
      return options.map((option) => {
        if (typeof option === 'string') {
          return { label: option, value: option };
        }
        return option;
      });
    }
  }
};
</script>

<style scoped>
.schema-form {
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border-radius: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.required {
  color: rgb(218, 61, 61);
}

.error-msg {
  display: block;
  margin-top: 0.25rem;
  color: rgb(218, 61, 61);
  font-size: 0.75rem;
  font-weight: 700;
}

label {
  display: block;
  color: rgb(56, 56, 53);
  font-size: 0.8rem;
  font-weight: 800;
  margin-bottom: 0.25rem;
}
</style>
